// sessions-store.svelte.ts — Module-level reactive store for the sidebar
// session list. Split out from +layout.svelte so WS-driven updates
// (session_name_update arriving via the active ws-client) can patch rows
// without the layout re-fetching /api/sessions on every event.

import { listSessions, type SessionInfo } from './api.ts';
import { isAutoNamerPlaceholder } from './session-naming.ts';

export type SessionActivity = 'running' | 'idle' | 'errored';

export interface SessionRow extends SessionInfo {
	activity: SessionActivity;
	lastActivityAt: number;
	/** Set once the user explicitly renames the session; gates auto-namer
	 *  broadcasts so a re-fire can't clobber a user-authored name even when
	 *  that name happens to look like a placeholder. */
	userRenamed: boolean;
}

const sessions = $state<SessionRow[]>([]);

export function getSessions(): SessionRow[] {
	return sessions;
}

function toRow(info: SessionInfo): SessionRow {
	return {
		...info,
		// "exited" maps to idle because an exited session with recoverable
		// history is still readable; we only light up the red dot on actual
		// errors surfaced via cli_disconnected or an is_error tool result.
		activity: 'idle',
		// Prefer the backend's lastActivityAt (newest user/assistant message
		// timestamp). Falls back to createdAt for sessions with no history.
		lastActivityAt: info.lastActivityAt ?? info.createdAt,
		userRenamed: false,
	};
}

export async function refreshSessions(): Promise<void> {
	const next = await listSessions();
	const nextRows = next.map(toRow);
	// Preserve in-memory state from the old array where the session still
	// exists — a silent refresh shouldn't blank out a running pulse or
	// a user-rename flag.
	const byId = new Map(sessions.map((s) => [s.sessionId, s]));
	for (const row of nextRows) {
		const prev = byId.get(row.sessionId);
		if (prev) {
			row.activity = prev.activity;
			row.lastActivityAt = Math.max(prev.lastActivityAt, row.lastActivityAt);
			row.userRenamed = prev.userRenamed;
		}
	}
	sessions.length = 0;
	sessions.push(...nextRows);
}

export function upsertSession(partial: Partial<SessionInfo> & { sessionId: string }): void {
	const i = sessions.findIndex((s) => s.sessionId === partial.sessionId);
	if (i >= 0) {
		Object.assign(sessions[i], partial);
	} else if (isFullSession(partial)) {
		sessions.unshift(toRow(partial));
	}
	// Partial for unknown session = silently ignore. A full refresh will
	// pick it up; we don't synthesise blanks (would flash placeholder rows).
}

/** Apply a backend-broadcast `session_name_update`. Returns the name that
 *  ended up on the row. Respects two gates:
 *   1. `userRenamed` wins outright — a user-authored name is never clobbered.
 *   2. Otherwise accept unless the current name doesn't look auto-generated
 *      AND the flag is still false (defensive: catches rows that pre-date
 *      markRenamed being wired on a given client). */
export function applyNameBroadcast(sessionId: string, name: string): string | undefined {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i < 0) return undefined;
	const row = sessions[i];
	if (row.userRenamed) return row.name;
	if (!isAutoNamerPlaceholder(row.name)) return row.name;
	row.name = name;
	return name;
}

export function markRenamed(sessionId: string): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i >= 0) sessions[i].userRenamed = true;
}

export function removeSession(sessionId: string): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i >= 0) sessions.splice(i, 1);
}

// Trash (SAY-1463): flip a row's soft-delete marker optimistically so the sidebar
// updates before the server round-trip. SessionList hides trashed rows; TrashList
// shows them. A later refreshSessions() reconciles with the server's deletedAt.
export function markTrashed(sessionId: string, at: number = Date.now()): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i >= 0) sessions[i].deletedAt = at;
}

export function markRestored(sessionId: string): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i >= 0) sessions[i].deletedAt = undefined;
}

/** Anchor a row's sidebar relative-time to an explicit timestamp — used by the chat
 *  page to pin the OPEN session's time to the turn-start (the user's last message)
 *  while a turn is in flight, so a hard refresh mid-turn reads "2h ago" (when you sent
 *  it) instead of resetting to createdAt (see sidebarAnchorWhileRunning in
 *  turn-activity.ts). Unlike markActivity this is an authoritative override of the
 *  relative-time anchor ONLY — no sticky/dot rules — and is idempotent (writes only on
 *  change) so the streaming-driven re-renders that feed it don't thrash reactivity. */
export function anchorSidebarTime(sessionId: string, at: number): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i >= 0 && sessions[i].lastActivityAt !== at) sessions[i].lastActivityAt = at;
}

export function markActivity(
	sessionId: string,
	activity: SessionActivity,
	at: number = Date.now(),
): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i < 0) return;
	const row = sessions[i];
	const prev = row.activity;
	// Errored is sticky until the next refresh or explicit clearErrored —
	// matches the session-mining intent: a 401/403 from last turn should
	// still warn the user after the CLI reconnects. Do NOT bump
	// lastActivityAt here: sticky means "keep the dot", not "this counts as
	// activity". Bumping made an idle-after-errored snapshot shove the row
	// to "just now" even when the user only clicked into the session.
	if (prev === 'errored' && activity === 'idle') {
		return;
	}
	row.activity = activity;
	// Only bump on real activity. Opening an already-idle session fires
	// session_phase=ready → markActivity('idle'); without this guard the row
	// jumps to "just now" just because the user clicked into the chat.
	if (activity === 'running' || prev === 'running' || activity === 'errored') {
		row.lastActivityAt = at;
	}
}

// Override for sticky-errored rows: called when the chat-page mounts and the
// first session_phase event reports a healthy phase. The stale red dot in the
// sidebar must clear even though markActivity('idle') would otherwise be
// swallowed by the sticky rule. lastActivityAt is intentionally preserved —
// clearing a stale red dot is not "activity".
export function clearErrored(sessionId: string): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i < 0) return;
	const row = sessions[i];
	if (row.activity === 'errored') row.activity = 'idle';
}

// Align a row's activity field to an authoritative snapshot WITHOUT bumping
// lastActivityAt. Used by the chat page for the first status callback after
// a fresh WS open: handleSessionSubscribe always sends a `status_change`
// reflecting the current state machine phase, but that snapshot is a state
// correction, not a turn event. Routing it through markActivity bumped the
// sidebar timestamp to "just now" whenever the snapshot was 'errored' or the
// row carried stale 'running' activity from a prior view — even though the
// user had merely opened the session.
export function syncActivity(sessionId: string, activity: SessionActivity): void {
	const i = sessions.findIndex((s) => s.sessionId === sessionId);
	if (i < 0) return;
	sessions[i].activity = activity;
}

/** The single sidebar-activity writer. Owns the snapshot-vs-live policy:
 *   - A cold-open snapshot (`snapshot:true`) aligns the dot WITHOUT bumping
 *     lastActivityAt, and maps a cold-open 'errored' to 'idle' — a prior-crash
 *     dot is stale because the next user send respawns the CLI cleanly.
 *   - A live transition routes through markActivity (sticky-errored + bump
 *     rules). A `null` live status is a no-op (pre-init window). */
export function applySessionStatus(
	sessionId: string,
	status: SessionActivity | 'compacting' | null,
	opts: { snapshot: boolean },
): void {
	if (opts.snapshot) {
		syncActivity(sessionId, status === 'running' || status === 'compacting' ? 'running' : 'idle');
		return;
	}
	if (status === 'running' || status === 'compacting') markActivity(sessionId, 'running');
	else if (status === 'idle') markActivity(sessionId, 'idle');
	else if (status === 'errored') markActivity(sessionId, 'errored');
	// status === null on a live transition: no-op (pre-init window).
}

// ── 1-minute ticker for relative-time renders ──────────────────────────────
// Sidebar consumers read `ticker` in a $derived to opt in to rerender.
// A single module-level interval is cheaper than one per row.

let ticker = $state(0);
let tickerHandle: ReturnType<typeof setInterval> | null = null;
let tickerSubscribers = 0;

export function getRelativeTimeTicker(): number {
	return ticker;
}

export function subscribeRelativeTicker(): () => void {
	tickerSubscribers += 1;
	if (tickerSubscribers === 1) {
		tickerHandle = setInterval(() => { ticker += 1; }, 60_000);
	}
	return () => {
		tickerSubscribers -= 1;
		if (tickerSubscribers === 0 && tickerHandle) {
			clearInterval(tickerHandle);
			tickerHandle = null;
		}
	};
}

function isFullSession(p: Partial<SessionInfo> & { sessionId: string }): p is SessionInfo {
	return typeof p.state === 'string' && typeof p.cwd === 'string' && typeof p.createdAt === 'number';
}
